import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Hidden } from '@mui/material';
import PageLayout from '../pagelayout';
import { useIntl } from 'react-intl';
import { formatLocaleNo, sortDates, todayAsFullDate } from '../date.utils';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import translations from './dashboard.i18n';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import { LanguageContext } from '../i18n/LanguageStore';
import { setISOWeek, setYear } from 'date-fns';
import Spinner from '../components/spinner/spinner';
import { PraksisaktivitetInfoDetaljertPsDto } from '../api/generated/praksissted-generated-api';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import Button from '../components/button';
import * as css from './dashboard.module.scss';
import { useNavigate } from 'react-router-dom';
import { useFormatMessageFor } from '../i18n/translation';
import classNames from 'classnames';

const Dashboard = () => {
  const { selectedLanguage } = useContext(LanguageContext);

  const { brukerApi } = useContext(PraksisstedApiContext);
  const { data: bruker } = useLoader(() => brukerApi.brukerinfoGet(), []);

  const labels = useFormatMessageFor({
    ...translations,
    welcome: {
      ...translations.welcome,
      values: {
        name: bruker ? `${bruker.fornavn} ${bruker.etternavn}` : 'UKJENT',
      },
    },
  });

  const toolbarLink: React.ReactNode = ToolbarLink({
    title: labels.linkToCalendar,
    exact: true,
    path: '/calendar',
    icon: <DateRangeIcon />,
  });

  const cardContent = (
    <>
      <h2>{labels.welcome}</h2>
      <p style={{ marginTop: '1em' }}>{labels.desc}</p>
    </>
  );

  return (
    <PageLayout isIndented title={labels.title} actionItems={toolbarLink}>
      <div className={css.dashboard}>
        <div className={css.card}>
          <div className={css.leftContainer}>
            <p className={css.date}>{todayAsFullDate(selectedLanguage)}</p>
            <Hidden only={['xs', 'sm']}>{cardContent}</Hidden>
          </div>
          <div className={css.rightContainer}>
            <div className={css.officeIllustration} />
          </div>
          <div>
            <Hidden only={['md', 'lg', 'xl']}>{cardContent}</Hidden>
          </div>
        </div>
        <div className={classNames(css.card, css.flexColumn)}>
          <h2>{labels.orders}</h2>
          <Orders />
        </div>
      </div>
    </PageLayout>
  );
};

enum ActivityTableType {
  AKTIV = 'AKTIV',
  KOMMENDE = 'KOMMENDE',
  GJENNOMFOERT = 'GJENNOMFOERT',
}

type DateSeparatedPraksisaktivitetInfoDetaljertPsDtoList = {
  [type in ActivityTableType]: PraksisaktivitetInfoDetaljertPsDto[];
};

const getActivityTableType = (
  activity: PraksisaktivitetInfoDetaljertPsDto,
  currentDate: Date,
): ActivityTableType => {
  const startDato = new Date(activity.startDato);
  const sluttDato = new Date(activity.sluttDato);

  if (startDato <= currentDate && currentDate <= sluttDato) {
    return ActivityTableType.AKTIV;
  } else if (startDato > currentDate) {
    return ActivityTableType.KOMMENDE;
  }

  return ActivityTableType.GJENNOMFOERT;
};

const formatActivities = (
  activities: PraksisaktivitetInfoDetaljertPsDto[],
  currentDate: Date,
): DateSeparatedPraksisaktivitetInfoDetaljertPsDtoList =>
  activities.reduce(
    (
      acc: DateSeparatedPraksisaktivitetInfoDetaljertPsDtoList,
      activity: PraksisaktivitetInfoDetaljertPsDto,
    ) => {
      const activityType = getActivityTableType(activity, currentDate);

      return { ...acc, [activityType]: acc[activityType].concat(activity) };
    },
    {
      [ActivityTableType.AKTIV]: [],
      [ActivityTableType.KOMMENDE]: [],
      [ActivityTableType.GJENNOMFOERT]: [],
    },
  );

// Exported for tests
export const Orders = () => {
  const { brukerApi } = useContext(PraksisstedApiContext);

  const currentDate = useMemo(() => new Date(Date.now()), []);
  const fromDate = setISOWeek(
    setYear(currentDate, currentDate.getFullYear() - 1),
    1,
  ).getTime();

  const { data: activities } = useLoader(() =>
    brukerApi.dashbordPraksisaktiviteterGet({
      queryParams: { fraDato: fromDate },
    }),
  );

  const orders = useMemo<JSX.Element[]>(() => {
    if (activities) {
      const formattedActivities = formatActivities(activities, currentDate);

      return Object.keys(formattedActivities).map((key) => {
        const keyWithType = key as ActivityTableType;

        return (
          <ActivityTable
            key={keyWithType}
            type={keyWithType}
            activities={formattedActivities[keyWithType]}
          />
        );
      });
    }

    return [];
  }, [activities, currentDate]);

  return activities ? <>{orders}</> : <Spinner />;
};

enum ActivityTableColumns {
  PERIODE,
  INSTITUSJON,
  PRAKSISTYPE,
  AKTIVITETSKRAV,
  HANDLING,
}

interface ActivityTableProps {
  type: ActivityTableType;
  activities: PraksisaktivitetInfoDetaljertPsDto[];
}

const ActivityTable: React.FC<React.PropsWithChildren<ActivityTableProps>> = ({
  type,
  activities,
}) => {
  const { formatMessage, locale } = useIntl();
  const navigate = useNavigate();

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (
      col: ActivityTableColumns,
      a: PraksisaktivitetInfoDetaljertPsDto,
      b: PraksisaktivitetInfoDetaljertPsDto,
    ) => {
      switch (col) {
        case ActivityTableColumns.PERIODE:
          const firstDateSort = sortDates(a.startDato, b.startDato);
          return firstDateSort === 0
            ? sortDates(a.sluttDato, b.sluttDato)
            : firstDateSort;
        case ActivityTableColumns.INSTITUSJON:
          return compare(a.sted.navn[0], b.sted.navn[0]);
        case ActivityTableColumns.PRAKSISTYPE:
          return compare(a.praksis.kode, b.praksis.kode);
        case ActivityTableColumns.AKTIVITETSKRAV:
          return compare(a.krav || '', b.krav || '');
      }
      return 0;
    },
    [compare],
  );

  const initActiveCol = ActivityTableColumns.PERIODE;

  const [activeCol, setActiveCol] = useState<number>(initActiveCol);
  const [sortedActivities, setSortedActivities] = useState<
    PraksisaktivitetInfoDetaljertPsDto[]
  >(
    // It makes most sense to sort upcoming activities in ascending order (closest to today)
    activities.sort((a, b) =>
      type === ActivityTableType.KOMMENDE
        ? sort(initActiveCol, b, a)
        : sort(initActiveCol, a, b),
    ),
  );
  const [search, setSearch] = useState<{ [column: number]: string }>({
    [ActivityTableColumns.PERIODE]: '',
    [ActivityTableColumns.INSTITUSJON]: '',
    [ActivityTableColumns.PRAKSISTYPE]: '',
    [ActivityTableColumns.AKTIVITETSKRAV]: '',
  });

  const sortActivitiesByActiveCol = useCallback(
    (col: ActivityTableColumns, ascending: boolean) => {
      setSortedActivities(
        [...activities].sort((a, b) =>
          ascending ? sort(col, b, a) : sort(col, a, b),
        ),
      );
    },
    [activities, sort],
  );

  const tableHeaders = useMemo(
    () => [
      {
        title: formatMessage({ id: translations.periodeHeader.id }),
        type: ActivityTableColumns.PERIODE,
      },
      {
        title: formatMessage({ id: translations.institusjonHeader.id }),
        type: ActivityTableColumns.INSTITUSJON,
      },
      {
        title: formatMessage({ id: translations.praksistypeHeader.id }),
        type: ActivityTableColumns.PRAKSISTYPE,
      },
      {
        title: formatMessage({ id: translations.aktivitetskravHeader.id }),
        type: ActivityTableColumns.AKTIVITETSKRAV,
      },
      {
        title: formatMessage({ id: translations.handlingHeader.id }),
        type: ActivityTableColumns.HANDLING,
      },
    ],
    [formatMessage],
  );

  const headers = useMemo(() => {
    const canSearch = [
      ActivityTableColumns.PERIODE,
      ActivityTableColumns.INSTITUSJON,
      ActivityTableColumns.PRAKSISTYPE,
      ActivityTableColumns.AKTIVITETSKRAV,
    ];

    return tableHeaders.map(({ title, type: columnType }) => (
      <TableCellTh
        id={`${type}-${columnType}`}
        key={`${title}-${type}-${columnType}`}
        colIndex={columnType}
        activeCol={columnType === activeCol}
        setActiveCol={setActiveCol}
        search={(_, value) => setSearch({ ...search, [columnType]: value })}
        canSearch={canSearch.includes(columnType)}
        sort={sortActivitiesByActiveCol}
        canSort={columnType !== ActivityTableColumns.HANDLING}
        initAscending={type === ActivityTableType.KOMMENDE}
      >
        {title}
      </TableCellTh>
    ));
  }, [tableHeaders, activeCol, search, sortActivitiesByActiveCol, type]);

  const handleSearchFilter = useCallback(
    (activitiesForSearch: PraksisaktivitetInfoDetaljertPsDto[]) =>
      activitiesForSearch.filter((activity) => {
        const filterDato =
          search[ActivityTableColumns.PERIODE].length === 0 ||
          `${formatLocaleNo(activity.startDato)} - ${formatLocaleNo(
            activity.sluttDato,
          )}`
            .toLowerCase()
            .indexOf(search[ActivityTableColumns.PERIODE].toLowerCase()) > -1;

        const filterInstitusjon =
          search[ActivityTableColumns.INSTITUSJON].length === 0 ||
          activity.sted.navn[0]
            .toLowerCase()
            .indexOf(search[ActivityTableColumns.INSTITUSJON].toLowerCase()) >
            -1;

        const filterPraksistype =
          search[ActivityTableColumns.PRAKSISTYPE].length === 0 ||
          `${activity.praksis.kode}: ${activity.praksis.navn}`
            .toLowerCase()
            .indexOf(search[ActivityTableColumns.PRAKSISTYPE].toLowerCase()) >
            -1;

        const filterAktivitetskrav =
          search[ActivityTableColumns.AKTIVITETSKRAV].length === 0 ||
          (activity.krav || '')
            .toLowerCase()
            .indexOf(
              search[ActivityTableColumns.AKTIVITETSKRAV].toLowerCase(),
            ) > -1;

        return (
          filterInstitusjon &&
          filterPraksistype &&
          filterAktivitetskrav &&
          filterDato
        );
      }),
    [search],
  );

  const filteredAndSortedRows = useMemo<PraksisaktivitetInfoDetaljertPsDto[]>(
    () => handleSearchFilter(sortedActivities),
    [sortedActivities, handleSearchFilter],
  );

  const handlingBtnLabel = formatMessage({ id: translations.handlingBtn.id });

  const rows = useMemo(
    () =>
      filteredAndSortedRows.map((row) => {
        const norwegianStartDate = formatLocaleNo(row.startDato);
        const norwegianEndDate = formatLocaleNo(row.sluttDato);

        return (
          <TableRow key={row.id}>
            <TableCellTd
              header={tableHeaders[ActivityTableColumns.PERIODE].title}
            >
              {`${norwegianStartDate} - ${norwegianEndDate}`}
            </TableCellTd>
            <TableCellTd
              header={tableHeaders[ActivityTableColumns.INSTITUSJON].title}
            >
              {row.sted.navn[0]}
            </TableCellTd>
            <TableCellTd
              header={tableHeaders[ActivityTableColumns.PRAKSISTYPE].title}
            >
              {`${row.praksis.kode}: ${row.praksis.navn}`}
            </TableCellTd>
            <TableCellTd
              header={tableHeaders[ActivityTableColumns.AKTIVITETSKRAV].title}
            >
              {row.krav}
            </TableCellTd>
            <TableCellTd
              header={tableHeaders[ActivityTableColumns.HANDLING].title}
            >
              <Button.Secondary
                onClick={() => navigate(`/calendar/activity/${row.id}`)}
              >
                {handlingBtnLabel}
              </Button.Secondary>
            </TableCellTd>
          </TableRow>
        );
      }),
    [filteredAndSortedRows, tableHeaders, navigate, handlingBtnLabel],
  );

  const caption = useMemo<string>(() => {
    switch (type) {
      case ActivityTableType.AKTIV:
        return formatMessage({ id: translations.aktiv.id });
      case ActivityTableType.GJENNOMFOERT:
        return formatMessage({ id: translations.gjennomfoert.id });
      case ActivityTableType.KOMMENDE:
        return formatMessage({ id: translations.kommende.id });
    }
  }, [formatMessage, type]);

  if (activities.length === 0) return <></>;

  return (
    <section className={css.activityTableWrapper}>
      <h3 className={css.caption}>{caption}</h3>
      <Table id={`activity-table-${type}`} tableWrapperClassName={css.table}>
        <TableHeader>
          <TableRow top>{headers}</TableRow>
        </TableHeader>
        <TableMain>{rows}</TableMain>
      </Table>
    </section>
  );
};

export default Dashboard;
